import React from 'react';
import Fuse from 'fuse.js';

export interface ResultItem<T> {
	item: T;
	matches: Array<[number, number]>;
}

export interface UseFullTextSearchOptions<T extends object> {
	items: T[];
	key: keyof T;
}
export interface UseFullTextSearchReturnValue<T> {
	inputProps: {
		value: string;
		onKeyUp: React.KeyboardEventHandler<HTMLInputElement>;
		onChange: React.ChangeEventHandler<HTMLInputElement>;
	};
	results: ResultItem<T>[];
}

export function useFullTextSearch<T extends object>({
	items,
	key,
}: UseFullTextSearchOptions<T>): UseFullTextSearchReturnValue<T> {
	const [query, setQuery] = React.useState('');

	const fuse = new Fuse(items, {
		includeMatches: true,
		shouldSort: false,
		minMatchCharLength: 1,
		findAllMatches: false,
		keys: [key as string],
		ignoreLocation: false,
		threshold: 0.5,
	});
	const [results, setResults] = React.useState<ResultItem<T>[]>(getSearchResults());

	function getSearchResults(searchVal: string = '') {
		if (!searchVal) {
			const mappedResults: ResultItem<T>[] = items.map(item => ({
				item,
				matches: [],
			}));
			return mappedResults;
		}
		const indexResults = fuse.search<T>(searchVal);
		const mappedResults: ResultItem<T>[] = indexResults.map(result => {
			const item: T = result.item;
			const matches: Array<[number, number]> = Array.from(
				result.matches?.find(match => match.key === key)?.indices.filter(([start, end]) => end - start > 0) ??
					[],
			);

			const resultItem: ResultItem<T> = {
				item,
				matches,
			};
			return resultItem;
		});
		return mappedResults;
	}

	function search(query?: string) {
		setResults(getSearchResults(query));
	}

	const onKeyUp: React.KeyboardEventHandler<HTMLInputElement> = event => {
		// setQuery(event.target.value);
		search(event.currentTarget.value);
	};

	const onChange: React.ChangeEventHandler<HTMLInputElement> = event => {
		setQuery(event.target.value);
		// search();
	};

	return {
		inputProps: {
			value: query,
			onKeyUp,
			onChange,
		},
		results,
	};
}
